import { supabase } from './supabaseClient';
import { canTransferLiability } from './deadlineService';
import type { DriverInvoiceStatus } from './driverInvoiceService';
import type { Driver, Hire, Ticket } from '../types';
import { normalizeTicketStatus } from '../utils/ticketStatus';

export type RecoveryAction =
  | 'none'
  | 'assign_driver'
  | 'transfer_first'
  | 'invoice_driver'
  | 'pay_then_recover'
  | 'await_driver_payment'
  | 'mark_recovered'
  | 'review_dispute';

export interface BillingSettingsSnapshot {
  admin_fee_type?: 'fixed' | 'percentage' | 'tiered' | 'none' | string | null;
  admin_fee_fixed_amount?: number | null;
  admin_fee_percentage?: number | null;
  admin_fee_tiers?: Array<{ min: number; max: number; fee: number }> | null;
}

export interface DriverResolutionContext {
  drivers?: Driver[];
  hireDriverByHireId?: Record<string, string>;
  linkedHire?: Hire | null;
  linkedDriver?: Driver | null;
}

export interface RecoveryDecision {
  action: RecoveryAction;
  invoiceable: boolean;
  blockingReasons: string[];
  warnings: string[];
  resolvedDriverId: string | null;
  recoverableAmount: number;
  adminFeeAmount: number;
  evidenceSummary: string[];
  label: string;
  hint: string;
}

const terminalStatuses = new Set(['cancelled', 'appeal_won', 'transferred']);

export const calculateAdminFeeAmount = (
  ticket: Pick<Ticket, 'amount'>,
  settings?: BillingSettingsSnapshot | null
): number => {
  if (!settings || settings.admin_fee_type === 'none') return 0;

  const ticketAmount = Number(ticket.amount || 0);
  if (!Number.isFinite(ticketAmount) || ticketAmount <= 0) return 0;

  if (settings.admin_fee_type === 'fixed') {
    return Number(settings.admin_fee_fixed_amount || 0);
  }

  if (settings.admin_fee_type === 'percentage') {
    return (ticketAmount * Number(settings.admin_fee_percentage || 0)) / 100;
  }

  if (settings.admin_fee_type === 'tiered' && Array.isArray(settings.admin_fee_tiers)) {
    const tier = settings.admin_fee_tiers.find((entry) => {
      const min = Number(entry.min);
      const max = Number(entry.max);
      return ticketAmount >= min && ticketAmount <= max;
    });
    return Number(tier?.fee || 0);
  }

  return 0;
};

export const resolveDriverIdForRecovery = (
  ticket: Ticket,
  context: DriverResolutionContext = {}
): string | null => {
  if (context.linkedDriver?.id) return context.linkedDriver.id;
  if (context.linkedHire?.driver_id) return context.linkedHire.driver_id;

  const hireMap = context.hireDriverByHireId || {};
  if (ticket.hire_id && hireMap[ticket.hire_id]) {
    return hireMap[ticket.hire_id];
  }

  const drivers = context.drivers || [];
  if (ticket.assigned_driver_email) {
    const email = ticket.assigned_driver_email.trim().toLowerCase();
    const byEmail = drivers.find((driver) => String(driver.email || '').trim().toLowerCase() === email);
    if (byEmail?.id) return byEmail.id;
  }

  if (ticket.assigned_driver_name) {
    const name = ticket.assigned_driver_name.trim().toLowerCase();
    const byName = drivers.filter((driver) => String(driver.name || '').trim().toLowerCase() === name);
    if (byName.length === 1 && byName[0]?.id) return byName[0].id;
  }

  return null;
};

export const decideRecoveryForTicket = (
  ticket: Ticket,
  context: DriverResolutionContext & {
    invoiceStatus?: DriverInvoiceStatus | null;
    billingSettings?: BillingSettingsSnapshot | null;
  } = {}
): RecoveryDecision => {
  const status = normalizeTicketStatus(ticket.status);
  const hasAssignedPerson = Boolean(
    ticket.hire_id ||
      ticket.assigned_driver_name ||
      ticket.assigned_driver_email ||
      context.linkedHire ||
      context.linkedDriver
  );
  const resolvedDriverId = resolveDriverIdForRecovery(ticket, context);
  const adminFeeAmount = calculateAdminFeeAmount(ticket, context.billingSettings);
  const recoverableAmount = Number(ticket.amount || 0) + adminFeeAmount;
  const invoiceStatus = context.invoiceStatus;
  const transferCheck = canTransferLiability(ticket);
  const linkedHire = context.linkedHire || null;

  const base: RecoveryDecision = {
    action: 'none',
    invoiceable: false,
    blockingReasons: [],
    warnings: [],
    resolvedDriverId,
    recoverableAmount,
    adminFeeAmount,
    evidenceSummary: [],
    label: 'No Recovery Action',
    hint: 'No driver recovery action is needed for this ticket.'
  };

  if (linkedHire || ticket.hire_id) base.evidenceSummary.push('Hire record linked');
  if (ticket.assigned_driver_name || context.linkedDriver?.name) base.evidenceSummary.push('Driver assigned');
  if (ticket.driver_invoice_id || invoiceStatus?.hasInvoice) base.evidenceSummary.push('Driver invoice exists');

  if (invoiceStatus?.status === 'paid' || ticket.driver_charge_status === 'paid') {
    return {
      ...base,
      action: 'mark_recovered',
      label: 'Driver Recovered',
      hint: 'Driver invoice has been paid. Close the ticket loop once the authority side is settled.'
    };
  }

  if (invoiceStatus?.status === 'disputed') {
    return {
      ...base,
      action: 'review_dispute',
      label: 'Review Dispute',
      hint: 'Driver has disputed the invoice. Review evidence before chasing payment or adjusting the invoice.'
    };
  }

  if (invoiceStatus?.hasInvoice || ticket.driver_invoice_id || ticket.driver_charge_status === 'invoiced') {
    return {
      ...base,
      action: 'await_driver_payment',
      label: 'Awaiting Driver Payment',
      hint: 'A driver invoice already exists. Continue reminders or payment follow-up from Driver Invoices.'
    };
  }

  if (terminalStatuses.has(status)) {
    return {
      ...base,
      blockingReasons: [`Ticket status is ${status.replace(/_/g, ' ')}`],
      hint: 'Terminal ticket statuses are not eligible for new driver invoices.'
    };
  }

  if (!hasAssignedPerson) {
    return {
      ...base,
      action: 'assign_driver',
      blockingReasons: ['No driver or hire is assigned'],
      label: 'Assign Driver',
      hint: 'Assign the hire or driver before recovering the charge.'
    };
  }

  if (!resolvedDriverId) {
    return {
      ...base,
      action: 'assign_driver',
      blockingReasons: ['Assigned driver details do not match a driver profile'],
      label: 'Match Driver Profile',
      hint: 'Create or select the matching driver profile before creating an invoice.'
    };
  }

  if ((linkedHire || ticket.hire_id) && transferCheck.allowed && status !== 'paid') {
    return {
      ...base,
      action: 'transfer_first',
      blockingReasons: ['Liability transfer window is still open'],
      label: 'Transfer First',
      hint: 'FleetMind recommends transferring liability before charging the driver while the legal transfer window is open.'
    };
  }

  if (status !== 'paid') {
    return {
      ...base,
      action: 'pay_then_recover',
      invoiceable: true,
      warnings: ['Authority side is not marked paid. Confirm liability and payment timing before sending the invoice.'],
      label: 'Pay Then Recover',
      hint: 'The driver can be invoiced, but confirm the authority-side payment or liability decision first.'
    };
  }

  return {
    ...base,
    action: 'invoice_driver',
    invoiceable: true,
    label: 'Invoice Driver',
    hint: 'Ticket is paid and assigned. Recover the PCN amount and configured admin fee from the driver.'
  };
};

export const getBillingSettingsForOrganisation = async (
  organisationId: string
): Promise<BillingSettingsSnapshot | null> => {
  const { data, error } = await supabase
    .from('organisation_billing_settings')
    .select('admin_fee_type, admin_fee_fixed_amount, admin_fee_percentage, admin_fee_tiers')
    .eq('organisation_id', organisationId)
    .single();

  if (error || !data) return null;
  return data as BillingSettingsSnapshot;
};

export const RecoveryDecisionService = {
  calculateAdminFeeAmount,
  decideRecoveryForTicket,
  getBillingSettingsForOrganisation,
  resolveDriverIdForRecovery
};
